import React, { useEffect, useRef, useState } from "react";
import { DatePicker, Table, Space, message, Modal, Pagination, Button, Input, Tabs, Tooltip, Drawer } from "antd";
import { BarChartOutlined, DeleteOutlined, ReloadOutlined, LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getLegalReport, deleteLegalReport, analyseLegalReport, retryLegalReport, precedence, submitLegalFeedback } from "../../apis/LegalCaseAnalyserAPI";
import FileUploadComponent from "./FileUploadComponent";
import "./fileupload.scss";
import PageTour from "../../components/PageTour/PageTour";
import { toggleTour } from "../../reducers/tourSlice";
import loader from '../../assets/images/loader.gif';
import SingleGaugeChart from "./SingleGaugeChart";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useLocation } from "react-router-dom";
import {getTitleFromPath} from "../../libs/utilities/helpers/getTitle"
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const LegalCaseAnalyser = () => {
  const dispatch = useDispatch();
  const [legalReports, setLegalReports] = useState([]);
  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs()]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [editedExtracts, setEditedExtracts] = useState(["", "", "", ""]);
  const [analysisResponse, setAnalysisResponse] = useState("");
  const [probability, setProbability] = useState(0);
  const location = useLocation();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [precedenceResponse, setPrecedenceResponse] = useState([]);
  const [rawMatchedCaseLaws, setRawMatchedCaseLaws] = useState(null);
  const [caseLawDrawerOpen, setCaseLawDrawerOpen] = useState(false);
  const [selectedCaseLaw, setSelectedCaseLaw] = useState(null);
  const [analysisHistoryId, setAnalysisHistoryId] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  const { confirm } = Modal;

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Are you sure you want to delete this report?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteReport(record);
      },
      onCancel() {
        // Do nothing if canceled
      },
    });
  };



  const columns = [
    {
      title: 'Case Name',
      dataIndex: 'case_name',
      key: 'case_name',
      sorter: (a, b) => a.case_name.localeCompare(b.case_name),
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        return (
          <span
            style={{
              color: status === 0 ? "orange" : (status === 1 ? "green" : "red"),
            }}
          >
            {status === 0 ? "In Progress" : (status === 1 ? "Completed" : "Failed")}
          </span>
        )
      }
    },
    {
      title: "Date",
      key: "date",
      render: (record) => dayjs(record.createdAt).format("DD-MM-YYYY"),
      sorter: (a, b) => {
        const dateA = dayjs(a.createdAt);
        const dateB = dayjs(b.createdAt);
        return dateB.isBefore(dateA) ? 1 : -1; // Sort latest first
      },
      defaultSortOrder: 'descend', // Default to descending order (latest first)
    },
    {
      title: "Time",
      key: "time",
      render: (record) => dayjs(record.updatedAt).format("HH:mm:ss"),
      sorter: (a, b) => {
        const timeA = dayjs(a.updatedAt);
        const timeB = dayjs(b.updatedAt);
        return timeB.isBefore(timeA) ? 1 : -1; // Sort latest first
      },
      defaultSortOrder: 'descend', // Default to descending order (latest first)
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Click on the icon to view extracts and analysis report">
            <BarChartOutlined
              style={{
                color: record.status === 1 ? "blue" : "gray",
                cursor: record.status === 1 ? "pointer" : "not-allowed"
              }}
              onClick={record.status === 1 ? () => handleViewExtract(record) : null}
            />
          </Tooltip>
          <Tooltip title=" Delete ">
            <DeleteOutlined
              style={{ color: 'red', cursor: 'pointer', marginLeft: '1rem' }}
              onClick={() => showDeleteConfirm(record)}
            />
          </Tooltip>
          {
            record.status === 2 && <Tooltip title="Retry">
              <ReloadOutlined onClick={() => retryReports(record._id)} style={{ color: "red", cursor: "pointer" }} />
            </Tooltip>
          }
        </Space>
      ),
    }
  ];


  const formatText = (text) => {
    if (!text) return "";
    let str = Array.isArray(text) ? text.join("\n") : text.toString();
    let htmlText = str.replace(/\n/g, '<br>');
    htmlText = htmlText.replace(/^\s+/, '');
    htmlText = htmlText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    htmlText = htmlText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    htmlText = htmlText.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
    return htmlText;
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const entities = {
      '&ldquo;': '\u201C', '&rdquo;': '\u201D', '&lsquo;': '\u2018', '&rsquo;': '\u2019',
      '&hellip;': '\u2026', '&amp;': '&', '&nbsp;': ' ', '&ndash;': '\u2013',
      '&mdash;': '\u2014', '&lt;': '<', '&gt;': '>',
    };
    return text.replace(/&[a-z]+;/gi, (match) => entities[match] || match);
  };

  const parseCaseLaws = (matchedCaseLaws) => {
    if (!matchedCaseLaws) return { error: 'no_data', rows: [] };
    let rawArray = [];
    if (!Array.isArray(matchedCaseLaws)) {
      if (matchedCaseLaws.id === -1 || matchedCaseLaws.name1 === 'ERROR') {
        return { error: 'error_state', rows: [] };
      }
      rawArray = [matchedCaseLaws];
    } else {
      rawArray = matchedCaseLaws;
    }
    if (rawArray.length === 0) return { error: 'no_data', rows: [] };
    
    // Flatten strings that might be multiple cases joined by <hr>
    let flattenedArray = [];
    for (const item of rawArray) {
        if (typeof item === 'string') {
            const splitItems = item.split(/<hr\s*\/?>/i);
            flattenedArray.push(...splitItems);
        } else {
            flattenedArray.push(item);
        }
    }

    // Filter out error objects or empty strings
    const validRaw = flattenedArray.filter(item => {
      if (typeof item === 'object' && item !== null) {
        return item.id !== -1 && item.id !== "-1" && item.name1 !== 'ERROR';
      }
      return typeof item === 'string' && item.trim().length > 0;
    });
    
    if (validRaw.length === 0) return { error: 'error_state', rows: [] };
    
    const rows = [];
    for (let i = 0; i < validRaw.length; i++) {
      const item = validRaw[i];
      let cleaned = '';
      let sourceUrl = '';
      let id = i;
      
      if (typeof item === 'string') {
         cleaned = item;
      } else if (item && typeof item === 'object') {
         if (!item.name2) continue;
         cleaned = item.name2;
         sourceUrl = item.name1 || '';
         id = item.id || i;
      } else {
         continue;
      }
      
      cleaned = cleaned
        .replace(/&lt;br&gt;/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<p>/gi, '\n\n') // convert <p> to newlines
        .replace(/<\/p>/gi, '');
        
      // Strip leading/trailing <br> tags which happen after <hr> split
      cleaned = cleaned.replace(/^(?:<br\s*\/?>\s*)+/i, '').replace(/(?:<br\s*\/?>\s*)+$/i, '');
        
      // Match delimiter with or without <br> tags, case-insensitive
      const parts = cleaned.split(/(?:<br\s*\/?>\s*)?-\|\|-(?:\s*<br\s*\/?>)?/i);
      
      if (parts.length < 5) {
        console.warn('[MatchedCaseLaws] Skipping id:', id, '- insufficient parts:', parts.length);
        continue;
      }
      
      rows.push({
        key: String(id),
        court: parts[0]?.trim() || '',
        caseDetails: parts[1]?.trim() || '',
        bench: parts[2]?.trim() || '',
        caseTitle: parts[3]?.trim() || '',
        representation: parts[4]?.trim() || '',
        citations: parts[5]?.trim() || '',
        judgment: parts.slice(6).join(' ').trim(),
        sourceUrl: sourceUrl,
      });
    }
    if (rows.length === 0) return { error: 'parse_failure', rows: [] };
    return { error: null, rows };
  };

  const handleViewExtract = (record) => {
    if (!record?.extracted_facts) {
      message.error("No extracted facts found for this case.");
      return;
    }
    const extracted = record.extracted_facts || {};
    const edited = record?.response?.edited_data || {};
    // Helper: prefer edited value only if it’s non-empty
    const getValue = (editedValue, extractedValue) => {
      if (Array.isArray(editedValue)) {
        return editedValue.length > 0 ? editedValue : extractedValue || [];
      }
      return editedValue && editedValue.toString().trim()
        ? editedValue
        : extractedValue || "";
    };
  
    const complaint = getValue(edited.complaint, extracted.complaint);
    const repudiation = getValue(edited.repudiation, extracted.repudiation);
    const evidence = getValue(edited.evidence, extracted.evidence);
    let matchedCases = record.matched_caselaws;

    setRawMatchedCaseLaws(matchedCases);
  
    let matchedCasesText = "";
    if (Array.isArray(matchedCases)) {
      matchedCasesText = matchedCases
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            if (item.id === -1 || item.name1 === "ERROR") return null;
            return item.name2 || "";
          }
          return null;
        })
        .filter(Boolean)
        .join("\n\n---\n\n");
    } else {
      matchedCasesText = typeof matchedCases === "string" ? matchedCases : "";
    }
  
    if (
      !complaint?.toString().trim() &&
      !repudiation?.toString().trim() &&
      (!Array.isArray(evidence) || evidence.length === 0) &&
      (!matchedCasesText.trim())
    ) {
      message.error("No extracted facts found for this case.");
      return;
    }
  
    setCurrentId(record._id);
  
    setEditedExtracts([
      formatText(complaint),
      formatText(repudiation),
      formatText(Array.isArray(evidence) ? evidence.join("\n") : ""),
      formatText(matchedCasesText),
    ]);

    // Load existing analysis if present
    const analysis = record?.response?.data || {};
    if (analysis.support_facts) {
      setAnalysisResponse(analysis.support_facts);
      setProbability(Number(analysis.win_score) / 100);
      setAnalysisHistoryId(analysis.analysis_history_id || null);
      setFeedbackRating(null);
      
      const validMatchedCases = (analysis.matched_caselaws || []).filter(
        item => item.id !== -1 && item.id !== "-1" && item.name1 !== "ERROR"
      );
      setPrecedenceResponse(validMatchedCases);
      setShowAnalysis(true);
    } else {
      setAnalysisResponse("");
      setProbability(0);
      setPrecedenceResponse([]);
      setAnalysisHistoryId(null);
      setFeedbackRating(null);
      setShowAnalysis(false);
    }
  
    setSelectedReport(record);
    setIsModalVisible(true);
  };
  


  const handleAnalyze = async () => {
    // Start from existing state
    let updatedExtracts = [...editedExtracts];
  
    // Overwrite only those refs that exist (user may not have touched all tabs)
    editableDivRefs.current.forEach((div, idx) => {
      if (div) {
        updatedExtracts[idx] = div.innerText;
      }
    });
  
    setEditedExtracts(updatedExtracts);
  
    // Save to localStorage
    if (currentId) {
      localStorage.setItem(`report_${currentId}_edits`, JSON.stringify(updatedExtracts));
    }
  
    const [complaint, repudiation, evidenceString, matchedCasesString] = updatedExtracts;
    if (!complaint.trim()) {
      message.destroy();
      message.error("No extracts found to analyze.");
      return;
    }
  
    setLoading(true);
  
    try {
      const evidenceArray = evidenceString
        .split("\n")
        .map(e => e.trim())
        .filter(e => e !== "");
  
      const payload = { _id: currentId, complaint, repudiation, evidence: evidenceArray, matched_caselaws: rawMatchedCaseLaws };
  
      const analysisResponse = await dispatch(analyseLegalReport(payload));
  
      if (analysisResponse?.payload?.statusCode === 200) {
        const { support_facts, win_score, matched_caselaws, analysis_history_id } = analysisResponse.payload.data;
        
        // Filter out error stubs (id: -1 or error names)
        const validMatchedCases = (matched_caselaws || []).filter(
          item => item.id !== -1 && item.id !== "-1" && item.name1 !== "ERROR"
        );
        
        setAnalysisResponse(support_facts);
        setProbability(Number(win_score) / 100);
        setPrecedenceResponse(validMatchedCases);
        setAnalysisHistoryId(analysis_history_id || null);
        setFeedbackRating(null);
        setShowAnalysis(true);
        fetchReports(pagination.current, pagination.pageSize, dateRange);
      } else {
        message.error("Analysis failed.");
      }
    } catch (error) {
      message.error("An error occurred during analysis.");
    } finally {
      setLoading(false);
    }
  };
  
  const retryReports = async (id) => {
    try {
      const res = await dispatch(retryLegalReport({ _id: id }))
      fetchReports(pagination.current, pagination.pageSize, dateRange);
      message.destroy();
      message.success('Retrying...')
    } catch (err) {
      // setRetryLoading(false)
    }
  }

  const fetchReports = async (page, pageSize, dates, showSuccessMessage = false) => {
    try {
      const data = {
        startDate: dates[0].startOf("day").format("YYYY-MM-DD"),
        endDate: dates[1].endOf("day").format("YYYY-MM-DD"),
        page,
        pageSize,
      };
      const response = await dispatch(getLegalReport(data));
      if (response ?.payload ?.data ?.documents) {
        setLegalReports(response.payload.data.documents);
        setPagination((prev) => ({
          ...prev,
          total: response.payload.data.total,
        }));

        if (showSuccessMessage) {
          message.destroy();
          message.success("Data updated successfully");
        }
      } else {
        message.error("Something went wrong");
        setLegalReports([]);
      }
    } catch (error) {
      message.error("Failed to fetch reports.");
    }
  };


  const deleteReport = async (record) => {
    try {
      const response = await dispatch(deleteLegalReport({ _id: record._id }));
      if (response.payload ?.statusCode === 200) {
        message.success("Case deleted successfully!");

        // Remove the deleted report from the state
        const updatedReports = legalReports.filter((report) => report._id !== record._id);
        setLegalReports(updatedReports);

        localStorage.removeItem(`report_${record._id}_edits`);


        // Check if the current page is still valid
        const totalPages = Math.ceil(updatedReports.length / pagination.pageSize);
        if (pagination.current > totalPages) {
          // If the current page is invalid (e.g., deleting from the last page), go to the previous page or first page
          setPagination((prev) => ({
            ...prev,
            current: totalPages > 0 ? totalPages : 1, // Go to last page or first page if no more pages
          }));
        }
      } else {
        message.error("Failed to delete the report.");
      }
    } catch (error) {
      message.error("An error occurred while deleting the report.");
    }
  };


  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      fetchReports(pagination.current, pagination.pageSize, dates);
    }
  };

  useEffect(() => {
    fetchReports(pagination.current, pagination.pageSize, dateRange);
  }, [pagination.current, pagination.pageSize, dateRange]);


  const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedReport(null);
    setShowAnalysis(false);
    setCaseLawDrawerOpen(false);
    setSelectedCaseLaw(null);
    setRawMatchedCaseLaws(null);
    setAnalysisHistoryId(null);
    setFeedbackRating(null);
    setFeedbackModalOpen(false);
    setFeedbackText("");
  };

  const sendFeedback = async (rating, feedback = "") => {
    if (!analysisHistoryId) {
      message.error("Unable to submit feedback for this analysis.");
      return false;
    }
    setFeedbackSubmitting(true);
    try {
      const payload = {
        analysis_history_id: analysisHistoryId,
        rating,
        feedback,
      };
      const response = await dispatch(submitLegalFeedback(payload));
      if (response?.payload?.statusCode === 200) {
        setFeedbackRating(rating);
        message.destroy();
        message.success("Thanks for your feedback!");
        return true;
      }
      message.error("Failed to submit feedback. Please try again.");
      return false;
    } catch (error) {
      message.error("An error occurred while submitting feedback.");
      return false;
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleLike = () => {
    if (feedbackRating === "like" || feedbackSubmitting) return;
    sendFeedback("like");
  };

  const handleDislike = () => {
    if (feedbackRating === "dislike" || feedbackSubmitting) return;
    setFeedbackText("");
    setFeedbackModalOpen(true);
  };

  const handleSubmitDislikeFeedback = async () => {
    if (!feedbackText.trim()) {
      message.error("Please tell us what went wrong before submitting.");
      return;
    }
    const success = await sendFeedback("dislike", feedbackText.trim());
    if (success) {
      setFeedbackModalOpen(false);
      setFeedbackText("");
    }
  };
  const handleExtractChange = (index, value) => {
    const updatedExtracts = [...editedExtracts];
    updatedExtracts[index] = value;
    setEditedExtracts(updatedExtracts);
  
    // Save to localStorage for persistence
    if (currentId) {
      localStorage.setItem(`report_${currentId}_edits`, JSON.stringify(updatedExtracts));
    }
  };
  
  const tourState = useSelector((state) => state.tour);
  const handleTourClose = () => {
    dispatch(toggleTour({ show: false }))
    // setShowHandAnimation(false);
  }

  const handleDownloadPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageW = doc.internal.pageSize.width;
    const pageH = doc.internal.pageSize.height;
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    // ── Design tokens ─────────────────────────────────────────────────────────
    const TITLE_SIZE  = 20;
    const H1_SIZE     = 13;
    const H2_SIZE     = 11;
    const BODY_SIZE   = 10;
    const SMALL_SIZE  = 9;
    const LINE_H      = 15;
    const SECTION_GAP = 18;
    const FIELD_GAP   = 11;
    const AFTER_FIELD = 7;

    // Palette
    const ACCENT       = [28,  80, 180];
    const ACCENT_LIGHT = [230, 237, 255];
    const GRAY_DARK    = [55,  55,  55];
    const GRAY_MID     = [110, 110, 110];
    const GRAY_LIGHT   = [210, 210, 210];
    const WHITE        = [255, 255, 255];
    const BLACK        = [20,  20,  20];

    // ── Helpers ───────────────────────────────────────────────────────────────

    const resetFont = () => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(BODY_SIZE);
      doc.setTextColor(...BLACK);
      doc.setDrawColor(...GRAY_LIGHT);
      doc.setFillColor(...WHITE);
      doc.setLineDashPattern([], 0);
    };

    const ensureSpace = (needed) => {
      if (y + needed > pageH - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const cleanText = (text) => {
      if (!text || typeof text !== "string") return "";
      return decodeHtmlEntities(text)
        .replace(/₹/g, "Rs. ")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/\*(.*?)\*/g, "$1")
        .replace(/<.*?>/g, "")
        .replace(/\t/g, " ")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/[ ]{3,}/g, "  ");
    };

    const printWrapped = async (text, fontSize, lh, color, style) => {
      if (!text || !cleanText(text).trim()) return;
      doc.setFont("helvetica", style || "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(...(color || BLACK));
      const lines = doc.splitTextToSize(cleanText(text), contentW);
      for (const line of lines) {
        ensureSpace(lh);
        doc.text(line, margin, y);
        y += lh;
      }
      resetFont();
    };

    const drawSectionHeading = (title) => {
      y += SECTION_GAP;
      ensureSpace(30);
      const fillH = 22;
      doc.setFillColor(...ACCENT_LIGHT);
      doc.roundedRect(margin - 6, y - 15, contentW + 12, fillH, 3, 3, "F");
      doc.setFillColor(...ACCENT);
      doc.rect(margin - 6, y - 15, 4, fillH, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(H1_SIZE);
      doc.setTextColor(...ACCENT);
      doc.text(title, margin + 6, y);
      y += 16;
      resetFont();
    };

    const drawSubHeading = (title) => {
      y += 8;
      ensureSpace(22);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(H2_SIZE);
      doc.setTextColor(...GRAY_DARK);
      doc.text(title, margin, y);
      doc.setDrawColor(...GRAY_LIGHT);
      doc.line(margin, y + 3, margin + contentW, y + 3);
      y += 14;
      resetFont();
    };

    const printField = async (label, value) => {
      if (!value || !cleanText(value).trim()) return;
      ensureSpace(FIELD_GAP + LINE_H);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(SMALL_SIZE);
      doc.setTextColor(...GRAY_MID);
      doc.text(label.toUpperCase(), margin, y);
      y += FIELD_GAP;
      await printWrapped(value, BODY_SIZE, LINE_H, BLACK, "normal");
      y += AFTER_FIELD;
    };

    // ── TITLE BANNER ──────────────────────────────────────────────────────────
    const caseName = selectedReport ? selectedReport.case_name : "Unknown Case";
    const sanitizedCaseName = caseName.replace(/[^a-z0-9]/gi, "_").substring(0, 50);
    const fileName = `${sanitizedCaseName}_Analysis_Report.pdf`;

    const bannerH = 52;
    doc.setFillColor(...ACCENT);
    doc.rect(0, 0, pageW, bannerH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(TITLE_SIZE);
    doc.setTextColor(...WHITE);
    doc.text(`Case Analysis Report`, margin, 33);
    y = bannerH + 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(H1_SIZE);
    doc.setTextColor(...GRAY_DARK);
    const caseNameLines = doc.splitTextToSize(caseName, contentW);
    for (const l of caseNameLines) {
      doc.text(l, margin, y);
      y += 16;
    }
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(1.5);
    doc.line(margin, y, margin + contentW, y);
    doc.setLineWidth(0.5);
    y += 18;
    resetFont();

    // ── GAUGE CHART ───────────────────────────────────────────────────────────
    const chartElement = document.querySelector("#gauge-chart");
    if (chartElement) {
      try {
        const canvas = await html2canvas(chartElement, { logging: false, useCORS: true, scale: 2 });
        const imgData = canvas.toDataURL("image/png");
        const imgProps = doc.getImageProperties(imgData);
        const imgWidth = 160;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        ensureSpace(imgHeight + 30);
        doc.addImage(imgData, "PNG", margin, y, imgWidth, imgHeight);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(H2_SIZE);
        doc.setTextColor(...GRAY_DARK);
        const pct = `${Math.round((probability || 0) * 100)}%`;
        doc.text(`Likelihood of HDFC Life winning the case:  ${pct}`, margin + imgWidth + 18, y + imgHeight / 2);
        y += imgHeight + 16;
        resetFont();
      } catch (e) {
        console.error("Error capturing chart:", e);
      }
    }

    // ── ANALYSIS ──────────────────────────────────────────────────────────────
    if (analysisResponse && analysisResponse.trim()) {
      drawSectionHeading("Analysis");
      await printWrapped(analysisResponse, BODY_SIZE, LINE_H, BLACK, "normal");
    }

    // ── EXTRACTED FACTS ───────────────────────────────────────────────────────
    const [complaint, repudiation, evidenceString] = editedExtracts;
    const hasExtracted = complaint?.trim() || repudiation?.trim() || evidenceString?.trim();

    if (hasExtracted) {
      drawSectionHeading("Extracted Facts");

      if (complaint?.trim()) {
        drawSubHeading("a) Complaint Extracted Facts");
        await printWrapped(complaint, BODY_SIZE, LINE_H, BLACK, "normal");
      }
      if (repudiation?.trim()) {
        drawSubHeading("b) Repudiation Extracted Facts");
        await printWrapped(repudiation, BODY_SIZE, LINE_H, BLACK, "normal");
      }
      if (evidenceString?.trim()) {
        drawSubHeading("c) Evidence Extracted Facts");
        await printWrapped(evidenceString, BODY_SIZE, LINE_H, BLACK, "normal");
      }
    }

    // ── MATCHED CASE LAWS ─────────────────────────────────────────────────────
    const { error: caseLawError, rows: caseLawRows } = parseCaseLaws(rawMatchedCaseLaws);
    if (!caseLawError && caseLawRows.length > 0) {
      drawSectionHeading("d) Matched Case Laws");

      for (let ci = 0; ci < caseLawRows.length; ci++) {
        const cl = caseLawRows[ci];
        y += 10;
        ensureSpace(36);

        // ── Case header band (height adapts to wrapped title lines) ───────────
        const LINE_H_HEADER = 14;   // line height inside the header band
        const PAD_V = 8;            // top/bottom padding inside the band

        // Set font before splitting so metrics match what jsPDF uses for rendering
        doc.setFont("helvetica", "bold");
        doc.setFontSize(H2_SIZE);
        const caseLabel = `Case ${ci + 1}${cl.caseTitle ? ":  " + cl.caseTitle : ""}`;
        const caseTitleLines = doc.splitTextToSize(caseLabel, contentW - 18);

        // Total band height: padding-top + all lines + padding-bottom
        const bandH = PAD_V + caseTitleLines.length * LINE_H_HEADER + PAD_V;
        ensureSpace(bandH + 10);

        const bandY = y;   // band starts exactly at current y

        // Light fill covering all lines
        doc.setFillColor(...ACCENT_LIGHT);
        doc.roundedRect(margin - 6, bandY, contentW + 12, bandH, 3, 3, "F");

        // Bold left accent bar — same height as the band
        doc.setFillColor(...ACCENT);
        doc.rect(margin - 6, bandY, 4, bandH, "F");

        // Render each title line inside the band — text baseline starts at bandY + PAD_V + LINE_H_HEADER
        doc.setTextColor(...ACCENT);
        const textStartY = bandY + PAD_V + LINE_H_HEADER - 2; // baseline of first line
        for (let li = 0; li < caseTitleLines.length; li++) {
          doc.text(caseTitleLines[li], margin + 8, textStartY + li * LINE_H_HEADER);
        }

        y = bandY + bandH + 16;  // move y past the band + comfortable gap before first field
        resetFont();


        await printField("Court", cl.court);
        await printField("Case Details", cl.caseDetails);
        await printField("Bench", cl.bench);
        await printField("Citations", cl.citations);
        await printField("Representation", cl.representation);
        await printField("Judgment", cl.judgment);

        if (ci < caseLawRows.length - 1) {
          ensureSpace(16);
          doc.setDrawColor(...GRAY_LIGHT);
          doc.setLineDashPattern([4, 3], 0);
          doc.line(margin, y + 4, margin + contentW, y + 4);
          doc.setLineDashPattern([], 0);
          y += 14;
        }
      }
      y += 12;
    }

    // ── PRECEDENCE ────────────────────────────────────────────────────────────
    resetFont();

    if (precedenceResponse && precedenceResponse.length > 0) {
      drawSectionHeading("Precedence Judgements");

      for (let i = 0; i < precedenceResponse.length; i++) {
        const judgement = precedenceResponse[i];
        ensureSpace(60);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(H2_SIZE);
        doc.setTextColor(...GRAY_DARK);
        doc.text(`${i + 1}.`, margin, y);

        if (judgement.url) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(SMALL_SIZE);
          doc.setTextColor(...ACCENT);
          const urlLines = doc.splitTextToSize(judgement.url, contentW - 16);
          doc.textWithLink(urlLines[0], margin + 16, y, { url: judgement.url });
          y += LINE_H;
          for (let li = 1; li < urlLines.length; li++) {
            ensureSpace(LINE_H);
            doc.text(urlLines[li], margin + 16, y);
            y += LINE_H;
          }
        } else {
          y += LINE_H;
        }

        if (judgement.match_score) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(SMALL_SIZE);
          doc.setTextColor(...GRAY_MID);
          doc.text(`Match Score: ${judgement.match_score}`, margin + 16, y);
          y += LINE_H;
        }

        resetFont();
        if (judgement.full_text) {
          await printWrapped(judgement.full_text, BODY_SIZE, LINE_H, GRAY_DARK, "normal");
        }
        y += 10;

        if (i < precedenceResponse.length - 1) {
          ensureSpace(12);
          doc.setDrawColor(...GRAY_LIGHT);
          doc.line(margin, y, margin + contentW, y);
          y += 12;
        }
      }
    }

    // ── FOOTER on every page ──────────────────────────────────────────────────
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(SMALL_SIZE - 1);
      doc.setTextColor(...GRAY_MID);
      doc.text(`${caseName}  |  Analysis Report`, margin, pageH - 24);
      doc.text(`Page ${p} of ${totalPages}`, pageW - margin, pageH - 24, { align: "right" });
      doc.setDrawColor(...GRAY_LIGHT);
      doc.line(margin, pageH - 32, pageW - margin, pageH - 32);
    }

    doc.save(fileName);
  };
  
  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  // Filter legalReports based on search input
  const filteredReports = legalReports.filter((report) =>
    report.case_name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const editableDivRefs = useRef([]);

  useEffect(() => {
    editableDivRefs.current = editableDivRefs.current.slice(0, editedExtracts.length);
  }, [editedExtracts.length]);


  return (
    <>
    <div className="heading-div">
        <p className="text5">
          {getTitleFromPath(location ?.pathname)}
        </p>
      </div>
    <div style={{ padding: "20px 0px" }}>
    
      <PageTour show={tourState.show} handleShow={handleTourClose} target="upload-documents-container" title="Click on the Create case button and start uploading  files to get analysis report" />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px"}}>
        <div style={{ width: "200px" }} >
          <Input
            placeholder="Search by case name"
            value={searchKeyword}
            onChange={handleSearchChange}
            style={{ height: "32px", borderRadius: "5px" }}
          />
        </div>

        <div style={{ display: "flex", gap: "10px", }}>

          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            disabledDate={disableFutureDates}
            style={{ height: "32px", }}
          />
          <FileUploadComponent onUploadSuccess={() => fetchReports(pagination.current, pagination.pageSize, dateRange)} />

          <Tooltip title="Refresh">
            <button
              className="btn-outlined"
              onClick={() => fetchReports(pagination.current, pagination.pageSize, dateRange, true)}
            >
              <div className='btn-outlined-content'>
                <div className='text'> <ReloadOutlined /> </div>
              </div>
            </button>
          </Tooltip>


        </div>
      </div>
      <Table
        dataSource={filteredReports.slice(
          (pagination.current - 1) * pagination.pageSize,
          pagination.current * pagination.pageSize
        )}
        columns={columns}
        rowKey="_id"
        pagination={false}
      />

      {/* Pagination Component */}
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={legalReports.length}
        onChange={(page) => {
          setPagination((prev) => ({
            ...prev,
            current: page,
          }));
        }}
        showSizeChanger={false}
        style={{ marginTop: "10px", float: "right" }}
      />


      {/* Modal */}
      <Modal
        title={!showAnalysis ? "Extracts" : "Analysis"}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        maskClosable={false}
        className="lca-analysis-modal"
      >
        <div className="lca-modal-body">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <img className="gif-loader" src={loader} alt="Loading..." />
          </div>
        ) : (
            <>
              {!showAnalysis ? (
                <>
                  {/* Extracts Section */}
                  <div style={{ marginBottom: "85px" }}>
                    <p style={{ color: "#717684", fontSize: "13px" }}>
                      Click on the tab to read or update the extracts
                    </p>
                    <Tabs
                      defaultActiveKey="1"
                      tabPosition="left"
                      style={{ height: 200, marginLeft: "-22px" }}

                    >
                      {editedExtracts.map((extract, index) => (
                        <Tabs.TabPane
                          tab={
                            index === 0
                              ? "Facts"
                              : index === 1
                                ? "Repudiation Reasons"
                                : index === 2
                                  ? "Evidences"
                                  : "Matched Cases"
                          }
                          key={index + 1}
                        >
                          {index === 3 ? (
                            (() => {
                              const { error, rows } = parseCaseLaws(rawMatchedCaseLaws);
                              if (error === 'error_state' || error === 'no_data') {
                                return (
                                  <div className="caselaw-empty-state">
                                    <div className="caselaw-empty-icon">⚖️</div>
                                    <p>No matched case laws available</p>
                                  </div>
                                );
                              }
                              if (error === 'parse_failure') {
                                return (
                                  <div className="caselaw-empty-state">
                                    <div className="caselaw-empty-icon">⚠️</div>
                                    <p>Unable to display case laws due to format issues</p>
                                  </div>
                                );
                              }
                              return (
                                <div className="caselaw-table-wrapper">
                                  <Table
                                    dataSource={rows}
                                    rowKey="key"
                                    size="small"
                                    scroll={{ x: 900, y: 200 }}
                                    pagination={false}
                                    className="caselaw-table"
                                    columns={[
                                      {
                                        title: 'Court',
                                        dataIndex: 'court',
                                        key: 'court',
                                        width: 150,
                                        render: (text) => (
                                          <Tooltip title={text} overlayClassName="caselaw-tooltip">
                                            <div className="caselaw-cell-clamp">{text}</div>
                                          </Tooltip>
                                        ),
                                      },
                                      {
                                        title: 'Case Details',
                                        dataIndex: 'caseDetails',
                                        key: 'caseDetails',
                                        width: 180,
                                        render: (text) => (
                                          <Tooltip title={text} overlayClassName="caselaw-tooltip">
                                            <div className="caselaw-cell-clamp">{text}</div>
                                          </Tooltip>
                                        ),
                                      },
                                      {
                                        title: 'Bench',
                                        dataIndex: 'bench',
                                        key: 'bench',
                                        width: 200,
                                        render: (text) => (
                                          <Tooltip title={text} overlayClassName="caselaw-tooltip">
                                            <div className="caselaw-cell-clamp">{text}</div>
                                          </Tooltip>
                                        ),
                                      },
                                      {
                                        title: 'Case Title',
                                        dataIndex: 'caseTitle',
                                        key: 'caseTitle',
                                        width: 220,
                                        render: (text) => (
                                          <Tooltip title={text} overlayClassName="caselaw-tooltip">
                                            <div className="caselaw-cell-clamp caselaw-cell-title">{text}</div>
                                          </Tooltip>
                                        ),
                                      },
                                      {
                                        title: 'Citations',
                                        dataIndex: 'citations',
                                        key: 'citations',
                                        width: 160,
                                        render: (text) => (
                                          <Tooltip title={text} overlayClassName="caselaw-tooltip">
                                            <div className="caselaw-cell-clamp">{text}</div>
                                          </Tooltip>
                                        ),
                                      },
                                      {
                                        title: 'Action',
                                        key: 'action',
                                        width: 110,
                                        fixed: 'right',
                                        render: (_, row) => (
                                          <Button
                                            type="link"
                                            size="small"
                                            style={{ padding: 0, fontSize: '12px' }}
                                            onClick={() => { setSelectedCaseLaw(row); setCaseLawDrawerOpen(true); }}
                                          >
                                            View Details
                                          </Button>
                                        ),
                                      },
                                    ]}
                                  />
                                </div>
                              );
                            })()
                          ) : (
                            <div
                              ref={el => editableDivRefs.current[index] = el}
                              contentEditable="true"
                              style={{
                                width: "100%",
                                borderRadius: "8px",
                                padding: "10px",
                                fontSize: "13px",
                                height: "260px",
                                overflow: "auto",
                                resize: "none",
                              }}
                              suppressContentEditableWarning={true}
                              dangerouslySetInnerHTML={{ __html: formatText(extract) }}
                            />
                          )}

                        </Tabs.TabPane>
                      ))}
                    </Tabs>
                  </div>

                  {/* Analyze Button */}
                  <div style={{ textAlign: "right" }}>
                    <Button type="primary" onClick={handleAnalyze}>
                      Analyze
                   </Button>
                  </div>
                </>
              ) : (
                  <>
                    {/* Analysis content */}
                    <div className="lca-analysis-row">
                      <div className="lca-analysis-gauge">
                        <SingleGaugeChart id="gauge-chart" value={probability} />
                      </div>
                      <div className="lca-analysis-divider" />
                      <div className="lca-analysis-text">
                        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                          {analysisResponse}
                        </ReactMarkdown>
                      </div>
                    </div>

                    {precedenceResponse && precedenceResponse.length > 0 && (
                      <div className="lca-precedence">
                        <h4 className="lca-precedence-title">Precedence</h4>
                        <div className="lca-precedence-body">
                          {precedenceResponse.map((judgement, index) => (
                            <div key={judgement.id || index} className="lca-precedence-item">
                              <h5>Judgement no.{index + 1}</h5>
                              <a href={judgement.url} target="_blank" rel="noopener noreferrer">
                                Judgement URL: {judgement.url}
                              </a>
                              <p>Match Score: {judgement.match_score}</p>
                              <p>{judgement.full_text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="lca-analysis-footer">
                      <div className="lca-feedback">
                        <span className="lca-feedback-label">Was this helpful?</span>
                        <Tooltip title="Like">
                          <button
                            type="button"
                            className={`lca-feedback-btn lca-feedback-like${feedbackRating === "like" ? " active" : ""}`}
                            onClick={handleLike}
                            disabled={feedbackSubmitting}
                            aria-label="Like this response"
                          >
                            {feedbackRating === "like" ? <LikeFilled /> : <LikeOutlined />}
                          </button>
                        </Tooltip>
                        <Tooltip title="Dislike">
                          <button
                            type="button"
                            className={`lca-feedback-btn lca-feedback-dislike${feedbackRating === "dislike" ? " active" : ""}`}
                            onClick={handleDislike}
                            disabled={feedbackSubmitting}
                            aria-label="Dislike this response"
                          >
                            {feedbackRating === "dislike" ? <DislikeFilled /> : <DislikeOutlined />}
                          </button>
                        </Tooltip>
                      </div>
                      <Button type="primary" onClick={handleDownloadPDF}>
                        Download as PDF
                      </Button>
                    </div>
                  </>
                )}
            </>
          )}

          {/* Dislike feedback popup (overlays the analysis modal) */}
          {feedbackModalOpen && (
            <div
              className="lca-feedback-overlay"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget && !feedbackSubmitting) {
                  setFeedbackModalOpen(false);
                  setFeedbackText("");
                }
              }}
            >
              <div className="lca-feedback-card">
                <h3 className="lca-feedback-modal-title">Help us improve</h3>
                <p className="lca-feedback-modal-subtitle">
                  Tell us what went wrong with this response.
                </p>
                <TextArea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Provide your comment here"
                  autoSize={{ minRows: 4, maxRows: 7 }}
                  maxLength={1000}
                  disabled={feedbackSubmitting}
                  autoFocus
                />
                <div className="lca-feedback-modal-actions">
                  <Button
                    onClick={() => { setFeedbackModalOpen(false); setFeedbackText(""); }}
                    disabled={feedbackSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSubmitDislikeFeedback}
                    loading={feedbackSubmitting}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Case Law Detail Drawer */}
      <Drawer
        title={
          <div className="caselaw-drawer-header">
            <span className="caselaw-drawer-title">{selectedCaseLaw?.caseTitle || 'Case Details'}</span>
          </div>
        }
        open={caseLawDrawerOpen}
        onClose={() => { setCaseLawDrawerOpen(false); setSelectedCaseLaw(null); }}
        width="75%"
        destroyOnClose
        className="caselaw-drawer"
        zIndex={1100}
      >
        {selectedCaseLaw && (
          <div className="caselaw-drawer-content">
            <div className="caselaw-detail-grid">
              <div className="caselaw-detail-item">
                <span className="caselaw-detail-label">Court</span>
                <span className="caselaw-detail-value">{selectedCaseLaw.court}</span>
              </div>
              <div className="caselaw-detail-item">
                <span className="caselaw-detail-label">Case Details</span>
                <span className="caselaw-detail-value">{selectedCaseLaw.caseDetails}</span>
              </div>
              <div className="caselaw-detail-item">
                <span className="caselaw-detail-label">Bench</span>
                <span className="caselaw-detail-value">{selectedCaseLaw.bench}</span>
              </div>
              <div className="caselaw-detail-item">
                <span className="caselaw-detail-label">Citations</span>
                <span className="caselaw-detail-value">{selectedCaseLaw.citations}</span>
              </div>
            </div>

            {selectedCaseLaw.representation && (
              <div className="caselaw-section">
                <h4 className="caselaw-section-title">Representation</h4>
                <p className="caselaw-section-body">{selectedCaseLaw.representation}</p>
              </div>
            )}

            {selectedCaseLaw.judgment && (
              <div className="caselaw-section">
                <h4 className="caselaw-section-title">Judgment</h4>
                <div className="caselaw-judgment-body">
                  {decodeHtmlEntities(selectedCaseLaw.judgment)
                    .split(/\r\r|\n\n/)
                    .map((para, i) => para.trim() ? <p key={i}>{para.trim()}</p> : null)
                  }
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>

    </div>
    </>
  );
};

export default LegalCaseAnalyser;
